import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import UpsertDoctorForm from 'components/doctors/UpsertDoctorForm';
import { dateInput } from 'components/final-form/DateInput';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithActive,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import Select from 'components/final-form/Select';
import { selectInput } from 'components/final-form/SelectInput';
import { textAreaInput } from 'components/final-form/TextAreaInput';
import { textInput } from 'components/final-form/TextInput';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import { AnamnesisSeveritySelect } from 'components/patient-entry/AnamnesisSeveritySelect';
import { ANAMNESIS_SEVERITY_COLORS } from 'components/patient-entry/bookings-table/InterventionBookingsTableView';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { PatientEntryConsumerRenderProps, withPatientEntryContext } from 'components/patient-entry/PatientEntryContext';
import { UpsertMode } from 'components/patient-entry/PatientEntryView';
import { DataEntry, DataEntryValue, Label } from 'components/patient-entry/styled';
import { previousDosesOptions } from 'components/patient-entry/vaccinationUtils';
import { FormApi, FormState } from 'final-form';
import createPmedDecorator from 'final-form-calculate-pmed/decorator';
import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Icon, List, ListItem, Modal, RadioProps } from 'semantic-ui-react';
import { findDoctorById, searchDoctorsForInput } from 'services/doctorServices';
import { getInterventionPrice } from 'services/interventionPriceServices';
import { getResourcesWithAvailableSlots } from 'services/patientCentralServices';
import { getAllActiveTreaters } from 'services/treaterServices';
import styled from 'styled-components';
import { AnamnesisSeverity, InterventionExternalCode } from 'ts-types/api.enums';
import {
  DoctorDto,
  InterventionDto,
  ProductDto,
  QuestionAnswerDto,
  QuestionDto,
  QuestionSetDto,
  ResourceDisplayDto,
  TariffDto,
  TreaterDto,
  TreatmentBookingDto,
  TreatmentReasonDto,
  UpsertDoctorDto
} from 'ts-types/api.types';
import { toIsoDateString } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { isTestIntervention, isVaccination } from 'utils/interventionExternalCodeUtils';
import { IndexSignature } from 'utils/praxTypes';
import { joinStrings } from 'utils/stringUtils';
import { required } from 'utils/validatorUtils';


const UpsertAnamnesisForm = styled.form`
`;

export const generateRadioButtonOptions = (
    ids: Array<string>,
    labels: Array<string>,
    groupName: string,
    getMessage: Function,
    values?: Array<string | number>): Array<RadioProps> => {

  return labels.map((label, index) => ({
        id: ids[index],
        label: getMessage(`${label}`),
        name: groupName,
        value: values ? values[index] : index
      }
  ));
};

export const convertValueToList = (value: string) => value && [value];
export const extractValueFromList = (value: Array<string>) => value && value.length === 1 ? value[0] : "";

interface Props extends PatientEntryConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    AuthConsumerRenderProps,
    MessageSourceProps {

  patientId: number;
  treatmentBooking: TreatmentBookingDto;
  mode: UpsertMode;
  onTreatmentUpdate: (treatmentBookingDto: TreatmentBookingDto, hasValidationError: boolean) => void;
  onQuestionSetChange: (questionSet?: QuestionSetDto) => void;
  onSubmit: (treatmentBookingDto?: TreatmentBookingDto) => void;
  setReferralDoctor: (doctor: DoctorDto | undefined) => void;
}

interface State {
  treaters: TreaterDto[];
  tariffs: Array<TariffDto>;
  tariffOptions?: Array<DropdownOption>;
  reasons: Array<TreatmentReasonDto>;
  reasonOptions?: Array<DropdownOption>;
  interventionMap: { [key: number]: InterventionDto };
  interventionOptions: DropdownOption[];
  questionSets: Array<QuestionSetDto>;
  questionSetMap: { [key: number]: QuestionSetDto };
  questionSetOptions?: DropdownOption[];
  anamnesisSeverityOptions?: DropdownOption[];
  containerOptions: DropdownOption[];
  resources: ResourceDisplayDto[];
  selectedQuestionSet?: QuestionSetDto;
  boosterDoseOptions: DropdownOption[],
  referralDoctorsMap: IndexSignature,
  referralDoctorOptions: DropdownOption[],
  selectedContainer?: string;
  selectedResourceId?: number;
  selectedProductId?: string;
  errorMessage?: string;
  currentQuestion: number;
  interventionPriceInsurance?: number;
  interventionPricePatient?: number;
  missingDoctorEmail: boolean;
  doctor?: UpsertDoctorDto,
  doctorId?: number,
  editDoctorModalOpen: boolean;
  requiredDataLoaded: boolean;
  cancelTokenSource: CancelTokenSource;
}

class UpsertAnamnesis extends Component<Props, State> {

  initialFormValues: Partial<TreatmentBookingDto>;

  constructor(props: Props) {
    super(props);

    const {
      tariffs,
      reasons,
      interventions: allInterventions,
      questionSets,
      language,
      getMessage
    } = this.props;

    const treatmentBooking = this.props.treatmentBooking;
    const questionSetMap = _.keyBy(questionSets, qs => qs.id);
    const selectedQuestionSet =
        treatmentBooking.questionSetId ? questionSetMap[treatmentBooking.questionSetId] : undefined;

    const anamnesisSeverityOptions = Object.values(AnamnesisSeverity).map(severity => {
      return {
        key: severity,
        text: getMessage(`enum.AnamnesisSeverity.${severity}`),
        value: severity,
        label: {color: ANAMNESIS_SEVERITY_COLORS[severity], empty: true, circular: true}
      }
    });

    let interventions = allInterventions.filter(
        intervention => intervention.active || (treatmentBooking.interventionId === intervention.id));

    const interventionMap = _.keyBy(interventions, "id");

    const interventionOptions = mapToDropdownOptionArrayWithActive(
        interventions, item => `${item.description} - ${item.durationInMinutes} min`);

    const containers = props.containers.filter(
        container => container.active && container.callCenterSupported);

    const containerOptions = mapToDropdownOptionArray(containers, "description", "id");
    containerOptions.push({
      key: "NO_CONTAINER",
      value: "NO_CONTAINER",
      text: getMessage("patientCentral.patientEntry.appointment.containerValue.NO_CONTAINER")
    });

    const resources: ResourceDisplayDto[] = [];

    const boosterDoseOptions = previousDosesOptions(getMessage);

    this.state = {
      treaters: [],
      tariffs: tariffs,
      tariffOptions: mapToDropdownOptionArray(tariffs, "description"),
      reasons: reasons,
      reasonOptions: mapToDropdownOptionArray(reasons, "description"),
      interventionMap: interventionMap,
      interventionOptions: interventionOptions,
      questionSets: questionSets,
      questionSetMap: questionSetMap,
      questionSetOptions: mapToDropdownOptionArrayWithTextGetter(questionSets, questionSet => {
        const nameMap: { [lang: string]: string } = {
          "de": questionSet.name,
          "en": questionSet.nameEn || questionSet.name,
          "fr": questionSet.nameFr || questionSet.name,
          "it": questionSet.nameIt || questionSet.name
        };
        return nameMap[language];
      }),
      selectedQuestionSet,
      selectedProductId: treatmentBooking.productId,
      anamnesisSeverityOptions: anamnesisSeverityOptions,
      containerOptions,
      resources,
      boosterDoseOptions,
      referralDoctorsMap: [],
      referralDoctorOptions: [],
      currentQuestion: 0,
      missingDoctorEmail: false,
      editDoctorModalOpen: false,
      requiredDataLoaded: false,
      cancelTokenSource: axios.CancelToken.source()
    }

    if (selectedQuestionSet) {
      this.props.onQuestionSetChange(selectedQuestionSet);
    }

    // Create initial values only on construct, since the value for date/time can change,
    // and will cause the form to be reinitialized; loosing any values already entered by the user.
    this.initialFormValues = {
      ...treatmentBooking
    };

    this.loadRequiredData(props);
  }

  loadRequiredData = async (props: Props) => {

    try {
      const treatmentBooking = this.props.treatmentBooking;

      if (this.initialFormValues && this.initialFormValues.referralDoctorId) {
        this.initialReferralDoctorData();
      }

      this.updateAvailableResources(treatmentBooking);

      const treaters = await getAllActiveTreaters(this.state.cancelTokenSource);
      this.setState({treaters: treaters});

    } finally {
      this.setState({requiredDataLoaded: true});
    }
  }

  initialReferralDoctorData = (): void => {
    if (this.initialFormValues && this.initialFormValues.referralDoctorId) {
      const referralDoctorId = this.initialFormValues.referralDoctorId;
      this.fetchSpecificReferralDoctor(referralDoctorId);
    }
  };

  fetchSpecificReferralDoctor = (referralDoctorId: number) => {
    const {cancelTokenSource} = this.state;
    findDoctorById(referralDoctorId, cancelTokenSource)
        .then(response => {
          const label = this.getDoctorLabel(response);
          const referralDoctorOption: DropdownOption = {
            key: referralDoctorId,
            text: label,
            value: referralDoctorId
          };

          const referralDoctor: DoctorDto = {
            ...response,
            id: referralDoctorId
          };
          this.props.setReferralDoctor(referralDoctor);

          this.setState({
            referralDoctorsMap: _.keyBy([response], "id"),
            referralDoctorOptions: [referralDoctorOption],
            missingDoctorEmail: !response.email
          });
        })
        .catch(noop)
  };

  private getDoctorLabel(doctor: UpsertDoctorDto | DoctorDto) {
    const docName = joinStrings(" ", doctor.firstName, doctor.name);
    const address = joinStrings(", ", doctor.city, doctor.street);
    return joinStrings(" - ", docName, address);
  }

  searchDoctors = (search: string): Promise<DoctorDto[]> => {

    const {cancelTokenSource} = this.state;

    return searchDoctorsForInput(search, cancelTokenSource, 75);
  };

  mapDoctors = (doctors: DoctorDto[]): DropdownOption[] => {

    this.setState({
      referralDoctorsMap: _.keyBy(doctors, "id")
    });

    return doctors.map(doctor => ({
      key: doctor.id,
      text: this.getDoctorLabel(doctor),
      value: doctor.id
    }));
  };

  updateAvailableResources = (treatmentBooking: TreatmentBookingDto) => {
    if (!treatmentBooking) {
      return;
    }

    const resourceId = treatmentBooking.resourceId;
    getResourcesWithAvailableSlots(
        undefined, undefined, this.state.cancelTokenSource)
        .then(results => {
          const filteredResources = results.filter(
              res => res.active || (resourceId && res.id === resourceId))
          this.setState({resources: filteredResources});
        });
  }

  onQuestionSetChange = (questionSetId: number, form: FormApi) => {
    const {selectedQuestionSet, questionSets} = this.state;

    if (selectedQuestionSet && selectedQuestionSet.id === questionSetId) {
      return;
    }

    const newQuestionSet = questionSets.find(questionSet => questionSet.id === questionSetId);

    let qaPairs: QuestionAnswerDto[] = [];
    if (newQuestionSet) {
      qaPairs = newQuestionSet.questions.map(question => ({
        questionId: question.id,
        questionText: question.text,
        questionTextEn: question.textEn,
        questionTextFr: question.textFr,
        questionTextIt: question.textIt,
        selectedAnswers: [],
        remarks: ""
      }));
    }

    form.batch(() => {
      form.change("questionSetId", questionSetId);
      form.change("questionAnswerPairs", qaPairs);
    });

    this.setState({
      selectedQuestionSet: newQuestionSet,
      currentQuestion: 0
    });

    this.props.onQuestionSetChange(newQuestionSet);
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  interventionDecorator = createPmedDecorator({
    field: "interventionId",
    updateOnPristine: false,
    updates: {
      container: (fieldValue, allValues?: Partial<TreatmentBookingDto>) => {
        if (fieldValue) {
          const intervention = this.props.getInterventionById(fieldValue);
          const externalCode = InterventionExternalCode[intervention!.externalCode];
          const isVaccinationIntervention = isVaccination(externalCode);
          if (isVaccinationIntervention) {
            return "NO_CONTAINER";
          }
        }
        return allValues!.container;
      }
    }
  });

  render() {
    if (!this.state.requiredDataLoaded) {
      return <></>;
    }

    return (
        <>
          <FinalForm
              onSubmit={this.submitTreatmentBookingForm}
              initialValues={this.initialFormValues}
              decorators={[this.interventionDecorator]}
              subscription={{submitting: true, pristine: true}}
              render={this.renderForm}
          />
          {this.renderUpsertDoctorModal()}
        </>
    );
  }

  submitTreatmentBookingForm = () => {
    this.props.onSubmit();
  }

  onFormChange = (formState: FormState) => {
    const {onTreatmentUpdate} = this.props;

    const values = formState.values as TreatmentBookingDto;
    onTreatmentUpdate(values, formState.hasValidationErrors);

    const productId = values.productId;
    if (productId) {
      this.setState({
        interventionPriceInsurance: undefined,
        interventionPricePatient: undefined
      });
      getInterventionPrice(productId, toIsoDateString(moment()), this.state.cancelTokenSource)
          .then(result => {
            const privateTariffInvoices = result.invoices.filter(inv => inv.privateTariff);
            if (privateTariffInvoices) {
              const totalAmountPrivateTariff = privateTariffInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPricePatient: totalAmountPrivateTariff});
            }

            const insuranceInvoices = result.invoices.filter(inv => !inv.privateTariff);
            if (insuranceInvoices) {
              const totalAmountInsurance = insuranceInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPriceInsurance: totalAmountInsurance});
            }
          })
          .catch(noop);
    }
  }

  renderForm = ({handleSubmit, form}: FormRenderProps): React.ReactNode => {

    const questionSetId = form.getState().values.questionSetId;

    return (
        <UpsertAnamnesisForm id="treatment-booking-anamnesis-form" onSubmit={handleSubmit}>
          <DataEntry key="treatment_section">
            {this.renderTreatmentSection(form, questionSetId)}
          </DataEntry>

          <FormSpy
              subscription={{values: true, hasValidationErrors: true}}
              onChange={this.onFormChange}
          />
        </UpsertAnamnesisForm>
    );
  }

  getExternalCodeForBilling = (externalCode: InterventionExternalCode): InterventionExternalCode => {
    const replacementInterventionForBilling: { [key: string]: InterventionExternalCode } = {
      [InterventionExternalCode.VACCINATION_DOSE_2]: InterventionExternalCode.VACCINATION_DOSE_1,
      [InterventionExternalCode.VACCINATION_CHILD_DOSE_2]: InterventionExternalCode.VACCINATION_CHILD_DOSE_1
    };

    const externalCodeForBilling: InterventionExternalCode =
        replacementInterventionForBilling[externalCode] || externalCode;

    return externalCodeForBilling;
  }

  renderTreatmentSection = (form: FormApi, questionSetId: number): JSX.Element => {

    const {mode, getMessage} = this.props;

    const {
      treaters,
      tariffOptions,
      reasonOptions,
      interventionMap,
      interventionOptions,
      questionSetOptions,
      anamnesisSeverityOptions,
      containerOptions,
      boosterDoseOptions,
      referralDoctorsMap,
      referralDoctorOptions,
      resources,
      interventionPriceInsurance,
      interventionPricePatient,
      selectedProductId
    } = this.state;

    const {products} = this.props;

    let hideTreatmentDetails = true;

    const disableEditIntervention = mode === UpsertMode.EDIT;

    const resourceOptions = resources.map(res => {
      return {
        key: res.id,
        text: res.description,
        value: res.id
      }
    });

    const values = form.getState().values;

    let showProductOptions = false;
    if (values.interventionId) {
      const intervention = this.props.getInterventionById(values.interventionId);
      const externalCode = InterventionExternalCode[intervention!.externalCode];
      showProductOptions = isTestIntervention(externalCode) || isVaccination(externalCode);
    }

    let freeProduct = false;
    if (values.productId) {
      freeProduct = products.find(pr => pr.id === values.productId)!.freeProduct;
    }

    const resourceId = values.resourceId;

    let treater = undefined;
    if (resourceId) {
      const resource = resources.find(r => r.id === resourceId);
      treater = treaters.find(t => t.id === resource!.treaterId);
    }

    let availableProducts: ProductDto[] = [];
    if (resourceId && values.interventionId) {
      const intervention = this.props.getInterventionById(values.interventionId);
      const externalCode = InterventionExternalCode[intervention!.externalCode];
      const externalCodeForBilling = this.getExternalCodeForBilling(externalCode);
      availableProducts = products.filter(product => {
        return (
                (!product.interventionId && product.externalCode === externalCodeForBilling)
                || (product.interventionId === intervention!.id)
            )
            && product.active || product.id === selectedProductId
            && !product.systemProduct
            && (!product.resourceIds
                || product.resourceIds.length === 0
                || product.resourceIds.includes(`${resourceId}`));
      })
    }
    const productOptions = mapToDropdownOptionArrayWithActive(
        availableProducts, "description");

    const selectMap: IndexSignature = {
      referralDoctorId: referralDoctorOptions,
      tariffId: tariffOptions,
      reasonId: reasonOptions,
      interventionId: interventionOptions,
      questionSetId: questionSetOptions,
      anamnesisSeverity: anamnesisSeverityOptions,
      product: productOptions,
      previousDoses: boosterDoseOptions
    }

    const dateInputField = dateInput({
      name: 'date',
      validate: required
    });

    const timeInputField = textInput({
      name: 'time',
      validate: required
    });

    const referralDoctorField = selectInput(
        {
          name: 'referralDoctorId',
          input: {
            value: values.referralDoctorId,
            onChange: (value: string) => {
              let referralDoctorId = null;
              let referralDoctor: DoctorDto | undefined = undefined;
              if (_.isNumber(value)) {
                referralDoctorId = parseInt(value);
                if (referralDoctorsMap[referralDoctorId]) {
                  referralDoctor = referralDoctorsMap[referralDoctorId];
                }
              }
              form.change("referralDoctorId", referralDoctorId);
              this.props.setReferralDoctor(referralDoctor);
              this.setState({
                missingDoctorEmail: !!(referralDoctor && !referralDoctor.email)
              });
            }
          },
          remoteSearch: true,
          remoteSearchMethod: this.searchDoctors,
          searchResultToOptionMapper: this.mapDoctors,
          clearable: true,
          options: referralDoctorOptions
        },
        selectMap
    );

    const treatmentFields = [
      selectInput({
        name: 'tariffId',
        validate: required
      }, selectMap),

      selectInput({
        name: 'reasonId',
        validate: required
      }, selectMap),

      textAreaInput({
        name: 'remarks'
      }, 2)
    ];
    const treatmentDetails = this.mapFieldRows(treatmentFields, 'anamnesis.form.treatment');

    const anamnesisFields = [
      selectInput({
        name: 'questionSetId',
        validate: required,
        input: {
          value: form.getState().values.questionSetId,
          onChange: (value: string) => {
            this.onQuestionSetChange(Number(value), form)
          }
        }
      }, selectMap)
    ];
    const anamnesisDetails = this.mapFieldRows(anamnesisFields, 'anamnesis.form');

    const interventionField = [
      selectInput({
        name: 'interventionId',
        validate: required,
        input: {
          value: form.getState().values.interventionId,
          onChange: (value: string) => {
            const prevIntervention = form.getState().values.interventionId;
            const questionSetId = form.getState().values.questionSetId;
            const interventionId: number = Number(value);
            form.change("interventionId", interventionId);

            const intervention = interventionMap[interventionId];

            if (intervention && InterventionExternalCode.VACCINATION_BOOSTER === intervention.externalCode) {
              form.change("previousDoses", 2);
            }

            if ((prevIntervention === undefined || questionSetId === undefined) && intervention) {
              const intervention = interventionMap[interventionId];
              if (intervention.questionSetId) {
                this.onQuestionSetChange(intervention.questionSetId, form);
              }
            }
          }
        },
        disabled: disableEditIntervention
      }, selectMap)
    ];
    const interventionDetails = this.mapFieldRows(interventionField, 'anamnesis.form.intervention');

    const interventionId = form.getState().values.interventionId;
    const intervention = interventionId ? interventionMap[interventionId] : undefined;
    const interventionExternalCode: InterventionExternalCode | undefined =
        intervention ? InterventionExternalCode[intervention.externalCode] : undefined;
    const showBoosterDoseOptions = intervention
        && InterventionExternalCode.VACCINATION_BOOSTER === intervention.externalCode;

    const showAnamnesisDetails = !intervention
        || (intervention && InterventionExternalCode.CLINICAL_QUESTIONS_TEST !== intervention.externalCode);

    let boosterDoseOptionsDetails = null;

    if (showBoosterDoseOptions) {
      const boosterDoseOptionField = [
        selectInput({
          name: 'previousDoses',
          validate: required,
          disabled: disableEditIntervention
        }, selectMap)
      ];
      boosterDoseOptionsDetails = this.mapFieldRows(boosterDoseOptionField, 'anamnesis.form');
    }

    return (
        <>
          <Label className="label" key={`treatment_label_datetime`}>
            {getMessage("anamnesis.form.treatment.dateTime")}:
          </Label>
          <div className="date-time-value">
            <DataEntryValue key={`treatment_value_date`}>
              <Field fluid {...dateInputField} />
            </DataEntryValue>

            <DataEntryValue key={`treatment_value_time`}>
              <Field fluid {...timeInputField} />
            </DataEntryValue>
          </div>

          <Label className="label" key={`treatment_label_referralDoctor`}>
            {getMessage("anamnesis.form.treatment.referralDoctorId")}:
          </Label>
          <div className="referral-doctor-value">
            <DataEntryValue key={`treatment_value_referralDoctor`}>
              <Field fluid {...referralDoctorField} />
              {values.referralDoctorId &&
                  <div className="doctor-email-container">
                    <Button type="button"
                            primary
                            size="mini"
                            onClick={() => this.openEditDoctorModal(values.referralDoctorId)}
                    >
                      {getMessage("msgEdit")}
                    </Button>
                    {
                        this.state.missingDoctorEmail &&
                        <div className="doctor-email-warning">
                          <Icon name="warning sign"/>
                          {getMessage("anamnesis.form.missingDoctorEmail")}
                        </div>
                    }
                  </div>
              }
            </DataEntryValue>
          </div>

          {hideTreatmentDetails ? <></> : treatmentDetails}

          <div className="questions-divider"/>
          {interventionDetails}
          {showBoosterDoseOptions && boosterDoseOptionsDetails}
          <div className="questions-divider"/>
          {anamnesisDetails}
          {
              questionSetId
              && <>
                {this.renderQuestions(form)}
              </>
          }

          {
            this.renderFieldRow(
                {
                  name: 'anamnesisSeverity',
                  component: AnamnesisSeveritySelect,
                  validate: required,
                  search: true,
                  options: anamnesisSeverityOptions
                },
                1,
                'anamnesis.form'
            )
          }

          {
              (!interventionExternalCode || !isVaccination(interventionExternalCode)) &&
              this.renderFieldRow(
                  {
                    id: 'container',
                    name: 'container',
                    component: Select,
                    validate: required,
                    search: true,
                    options: containerOptions,
                    input: {
                      value: form.getState().values.container,
                      onChange: (value: string) => {
                        form.change("container", value);
                      }
                    }
                  },
                  3,
                  'anamnesis.form'
              )
          }

          {
            this.renderFieldRow(
                {
                  id: 'resourceId',
                  name: 'resourceId',
                  component: Select,
                  validate: required,
                  search: true,
                  options: resourceOptions
                },
                4,
                'anamnesis.form'
            )
          }

          {
            treater &&
              <>
                <Label className="label" key={`treatment_label_treaterId`}>
                  {getMessage("resources.treater")}:
                </Label>
                <DataEntryValue key={`treatment_value_treaterId`}>
                  <span>{`${treater.firstName} ${treater.name}`}</span>
                </DataEntryValue>
              </>
          }

          {
              showProductOptions &&
              <>
                {
                  this.renderFieldRow(
                      {
                        name: 'productId',
                        component: Select,
                        validate: required,
                        search: true,
                        options: productOptions
                      },
                      2,
                      'anamnesis.form'
                  )
                }

                {
                    values.productId !== undefined &&
                    <>
                      <Label className="label"></Label>
                      <DataEntryValue>
                        {products.find(pr => pr.id === values.productId)!.comments}
                      </DataEntryValue>
                    </>
                }

                {
                    interventionPriceInsurance !== undefined && !freeProduct &&
                    <>
                      <Label className="label">{getMessage("anamnesis.form.amountInsurance")}:</Label>
                      <DataEntryValue>
                        {interventionPriceInsurance}&nbsp;CHF
                      </DataEntryValue>
                    </>
                }

                {
                    interventionPricePatient !== undefined && !freeProduct &&
                    <>
                      <Label className="label">{getMessage("anamnesis.form.amountPatient")}:</Label>
                      <DataEntryValue>
                        {interventionPricePatient}&nbsp;CHF
                      </DataEntryValue>
                    </>
                }
                {
                    freeProduct &&
                    <>
                      <label/>
                      <span style={{fontStyle: "italic", color: "#0096FF"}}>
                      {getMessage("product.freeProduct.message")}
                    </span>
                    </>
                }
              </>
          }
        </>
    );
  }

  previousQuestion = () => {
    const {currentQuestion} = this.state;

    this.setState({
      currentQuestion: Math.max(currentQuestion - 1, 0)
    })
  }

  nextQuestion = () => {
    const {selectedQuestionSet, currentQuestion} = this.state;

    this.setState({
      currentQuestion: Math.min(currentQuestion + 1, selectedQuestionSet!.questions.length - 1)
    })
  }

  setQuestionValue = (fieldPath: string, value: boolean, form: FormApi) => {
    form.change(fieldPath, [value]);
    this.nextQuestion();
  }

  renderQuestions = (form: FormApi): JSX.Element => {
    const {getMessage} = this.props;
    const {currentQuestion, questionSetMap} = this.state;

    const values: TreatmentBookingDto = form.getState().values as TreatmentBookingDto;

    const questionSetId = values.questionSetId;
    if (!questionSetId) {
      return <></>;
    }

    const questionSet = questionSetMap[questionSetId];
    if (!questionSet) {
      return <></>;
    }

    const questionMap = _.keyBy(questionSet.questions, q => q.id);

    const currentQuestionIx = currentQuestion < questionSet.questions.length ? currentQuestion : 0;

    const qaPairs = values.questionAnswerPairs;

    return (
        <>
          <div className="label"/>
          <div className="question-num-label">
            {getMessage("anamnesis.form.questionNum")}
            <span className="no-break">({`${currentQuestionIx + 1}/${questionSet.questions.length}`})</span>
          </div>

          <div className="question-container">
            <Button
                className="button-back"
                type="button"
                icon size="mini"
                disabled={currentQuestionIx <= 0}
                onClick={this.previousQuestion}
            >
              <Icon name="chevron left"/>
            </Button>

            <div className="question">
              {
                qaPairs.map((qaPair, index: number) => {
                  const question: QuestionDto = questionMap[qaPair.questionId];

                  if (!question) {
                    return <div key={`question-wrapper-unknown-q-${qaPair.questionId}`}/>
                  }

                  const wrapperClass = index !== currentQuestionIx ? "inactive" : "";
                  return <div key={`question-wrapper-${question!.id}`} className={`question-wrapper ${wrapperClass}`}>
                    {this.renderSingleQuestion(`questionAnswerPairs[${index}]`, index, question, form)}
                  </div>;
                })
              }
            </div>

            <Button className="button-next"
                    type="button"
                    icon
                    size="mini"
                    disabled={currentQuestionIx >= questionSet.questions.length - 1}
                    onClick={this.nextQuestion}
            >
              <Icon name="chevron right"/>
            </Button>
          </div>
        </>
    );
  }

  renderSingleQuestion = (questionAnswerFieldPath: string, index: number, question: QuestionDto, form: FormApi) => {
    const textMap: { [lang: string]: string } =
        {
          "de": question.text,
          "en": question.textEn || question.text,
          "fr": question.textFr || question.text,
          "it": question.textIt || question.text
        };
    return (
        <Fragment key={`question-fragment-${index}`}>
          <Label className="question-text" key={`question_label_${question.questionKey}`}>
            {textMap[this.props.language]}
          </Label>
          <DataEntryValue className="answer-value" key={`question_value_${question.questionKey}`}>
            {this.renderAnswerByType(`${questionAnswerFieldPath}.selectedAnswers`, index, question, form)}
          </DataEntryValue>
        </Fragment>
    );
  }

  renderAnswerByType = (fieldPath: string, index: number, question: QuestionDto, form: FormApi) => {
    const {getMessage} = this.props;
    const {questionType} = question;
    switch (questionType) {
      case 'MULTI_ANSWER':
        const answers = question.possibleAnswers.map((answer, index) => (
                <ListItem key={`${fieldPath}_${index}`}>
                  <Field
                      name={fieldPath}
                      component="input"
                      type="checkbox"
                      value={answer}
                  />
                  {' ' + getMessage(answer)}
                </ListItem>
            )
        )
        return (
            <List>
              {answers}
            </List>
        );
      case 'SINGLE_ANSWER':
        return (
            <Field
                name={fieldPath}
                component={RadioGroup}
                parse={convertValueToList}
                format={extractValueFromList}
                radioDefinitions={
                  generateRadioButtonOptions(
                      question.possibleAnswers,
                      question.possibleAnswers,
                      fieldPath,
                      getMessage,
                      question.possibleAnswers
                  )
                }
            />
        );
      case 'FREE_TEXT':
        return (
            <Field
                name={fieldPath}
                component={Input}
                fluid
                placeholder={getMessage('insert.answer')}
                parse={convertValueToList}
                format={extractValueFromList}
            />
        );
      case 'YES_NO':
      default:
        const qa = _.get(form.getState().values, fieldPath);
        return (
            <>
              <Button
                  className="yesno-button"
                  type="button"
                  size="mini"
                  primary={this.answerEquals(qa, true)}
                  onClick={() => this.setQuestionValue(fieldPath, true, form)}
              >
                {getMessage("anamnesis.answer.yes")}
              </Button>

              <Button
                  className="yesno-button"
                  type="button"
                  size="mini"
                  primary={this.answerEquals(qa, false)}
                  onClick={() => this.setQuestionValue(fieldPath, false, form)}
              >
                {getMessage("anamnesis.answer.no")}
              </Button>
            </>
        );
    }
  }

  answerEquals = (answers: string[], value: any): boolean => {
    return answers && answers.length === 1 && (answers[0] === value || answers[0] === "" + value);
  }

  mapFieldRows = (fields: Array<any>, messageKey?: string) => {

    const {getMessage} = this.props;

    return fields.map((fieldProps, index) => {
      return this.renderFieldRow(fieldProps, index, messageKey);
    });
  };

  private renderFieldRow(
      fieldProps: any,
      index: number,
      messageKey: string | undefined) {

    const {getMessage} = this.props;

    const labelKey = messageKey ? `${messageKey}.${fieldProps.name}` : fieldProps.name;

    const className = fieldProps.className;
    const labelClassName = className ? `${className}-label` : "";
    const valueClassName = className ? `${className}-value` : "";

    return (
        <Fragment key={`treatment_input_row_${index}`}>
          <Label className={`${labelClassName} label`} key={`treatment_label_${index}`}>{getMessage(labelKey)}:</Label>
          <DataEntryValue key={`treatment_value_${index}`} className={`${valueClassName}`}>
            <Field fluid {...fieldProps} />
          </DataEntryValue>
        </Fragment>
    );
  };

  onSaveDoctor = () => {
    const {doctorId} = this.state;
    if (doctorId) {
      this.fetchSpecificReferralDoctor(doctorId);
    }
    this.hideDoctorModal();
  };

  openEditDoctorModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findDoctorById(id, cancelTokenSource).then(response => {
        this.showDoctorModal(id, response);
      }).catch(noop);
    }
  };

  showDoctorModal = (id?: number, doctor?: UpsertDoctorDto) => {

    this.setState(({}: State) => {
      return {
        editDoctorModalOpen: true,
        doctor: doctor,
        doctorId: id
      }
    });
  };

  hideDoctorModal = () => {

    this.setState(({}: State) => ({
      editDoctorModalOpen: false,
      doctorId: undefined,
      doctor: undefined
    }));
  };

  renderUpsertDoctorModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {editDoctorModalOpen, doctor, doctorId} = this.state;

    return (
        <Modal
            open={editDoctorModalOpen}
            onClose={() => this.hideDoctorModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("doctors")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertDoctorForm
                doctor={doctor}
                doctorId={doctorId}
                onSave={this.onSaveDoctor}
                onCancel={this.hideDoctorModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(
    withAuthContext(
        withPatientCentralRefDataContext(
            withPatientEntryContext(UpsertAnamnesis))));
